import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Modal, Button } from '../common';
import { fetchAllProjectProblems, patchProject } from './ocrApi';
import { splitByStage, STAGES, STAGE_TEXT } from './promoteStage';

/**
 * 등록 전 공통 라벨 관리 (⑥ 문제 등록).
 *
 * 이 창이 여는 자리는 ⑥의 [문제 등록 실행]/[다시 등록]과 결과 카드의 [재시도] **둘 다**다.
 * 창이 곧 확인창이므로(종전 window.confirm 을 흡수했다) promote 를 부르는 자리는 이 창의
 * 버튼 하나여야 한다 — 창 밖에 호출부가 남으면 확인 없이 되돌릴 수 없는 등록이 실행된다.
 *
 * **2026-08-30 계약 변경으로 크게 줄었다.** 종전에는 프로젝트 키워드를 고칠 API 가 없어서
 * 「같은 값을 전 문제의 extra_labels 에 일괄 부착」하는 우회를 썼는데(문제마다 상세 GET + PATCH,
 * 상세 GET 은 호출마다 S3 원본을 크롭한다), 이제 `PATCH /projects/{id} {keywords}` 가 열렸다
 * (BE-20260830 §3-2). 우회와 그 판정 모듈(promoteLabelPlan)을 함께 걷어냈다.
 *
 * ⚠ 바뀐 키워드는 **이미 등록된 문제에 소급되지 않는다.** 서버는 재승격할 때 반영하는데
 * 이 저장소는 재승격(덮어쓰기)을 화면에서 유발하지 않는다(오너 결정 2026-08-30).
 * 그래서 실질적으로 「지금부터 등록되는 문제에만」이고, 창이 그렇게 말한다.
 */

const CHIP = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]';

function PromoteLabelModal({
  isOpen, onClose, projectId, keywords, getAccessToken, onPromote, promoting, onKeywordsSaved,
  // 첫 등록이 ②·③ 을 잠근다는 경고(부르는 쪽이 만든 문장 | null) — 등록 1건이 프로젝트를 컨펌대기(400)로 옮기면
  // 업로드·실행·매칭이 423 이 되어, 아직 못 읽은 쪽이나 매칭 안 한 쪽이 영영 문제로 올라오지 못한다(2026-09-25 분석).
  // 막지는 않는다 — 그 쪽들을 버리고 등록하는 것도 정당한 선택이다.
  lockWarning = null,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [truncated, setTruncated] = useState(false);

  const [words, setWords] = useState([]);
  // baseline = 「서버에 있다고 우리가 아는 값」.
  // props.keywords 를 기준으로 삼지 않는다 — 패널이 5초마다 상세를 다시 읽어(POLL_MS) detail 을
  // 갈아끼우므로 keywords 는 **내용이 같아도 배열 정체성이 매번 바뀐다.** 그 값에 기대면 폴링 한 번에
  // 사용자가 친 키워드가 되돌아가고 [저장]이 잠긴 채로 굳는다. 저장에 성공했을 때만 앞으로 민다.
  const [baseline, setBaseline] = useState([]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // 서버 값은 창이 열릴 때 한 번만 들여온다. 열려 있는 동안의 상세 갱신은 무시한다.
  const initedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchAllProjectProblems(projectId, getAccessToken);
      setTruncated(!!d.truncated);
      setRows(d.items);
    } catch (e) {
      setError(e.message || '문제 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [projectId, getAccessToken]);

  useEffect(() => {
    if (!isOpen) { initedRef.current = false; return; }
    if (initedRef.current) return; // 폴링이 keywords 정체성을 바꿔도 다시 들여오지 않는다
    initedRef.current = true;
    const kw0 = Array.isArray(keywords) ? keywords.filter(Boolean) : [];
    setWords(kw0);
    setBaseline(kw0);
    setInput('');
    setSaved(false);
    load();
  }, [isOpen, keywords, load]);

  // 등록 대상 = 아직 등록 안 됐고 **AI 검수까지 끝난 것**(분석 완료 + 그 결과를 사람이 승인).
  // 분석이 끝나면 그 문제는 검수대기(100)로 되돌아오므로, 승인(200)까지 와야 사람이 본 것이다.
  //
  // 이미 등록된 행을 **일부러 뺀다.** 서버는 2026-08-30 부터 재승격을 허용해 바뀐 행을 덮어쓰지만,
  // 덮어쓰기는 화면에서 유발하지 않는다(오너 결정). 계약이 막아서가 아니라 **정책으로 안 하는 것**이니
  // 「이제 되는데?」로 이 필터를 풀면 그 순간 덮어쓰기가 시작된다.
  const stage = useMemo(() => splitByStage(rows), [rows]);
  const targets = stage.targets;
  const promotedCount = stage.counts.promoted;

  // 구분자가 널 문자인 이유: 공백이면 「가 나」 하나와 「가」·「나」 둘이 같은 문자열이 된다.
  const dirty = words.join('\u0000') !== baseline.join('\u0000');

  const addWord = (raw) => {
    const w = String(raw || '').trim();
    if (!w || w.length > 100) return;
    setWords((prev) => (prev.includes(w) ? prev : [...prev, w]));
    setSaved(false);
  };

  const onKeyDown = (e) => {
    if (e.nativeEvent.isComposing) return; // 한글 IME 조합 중 Enter 는 조합 확정이라 무시
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addWord(input);
      setInput('');
    } else if (e.key === 'Backspace' && !input && words.length > 0) {
      setWords((prev) => prev.slice(0, -1));
      setSaved(false);
    }
  };

  /**
   * 공통 라벨 저장 — 프로젝트 키워드만 고친다. 은행에는 여기서 쓰지 않는다.
   *
   * 반영은 **[등록 실행]이 한다.** 키워드를 바꾸면 서버가 그 프로젝트의 등록분 전부에
   * needs_repromote 를 세우고, 등록을 다시 누르면 라벨을 다시 계산해 은행에 반영한다
   * (BE-20260830 §3-2). 바뀐 것이 없는 행은 서버가 'unchanged' 로 건너뛴다.
   *
   * ⚠ 저장 뒤 **목록을 다시 읽어야 한다.** needs_repromote 는 서버가 방금 세운 값이라,
   * 다시 읽지 않으면 [등록 실행]이 갱신 대상을 0건으로 본다 — 저장하고 재등록해도 아무 일이
   * 일어나지 않던 원인이 이것이다.
   */
  const saveKeywords = async () => {
    if (saving || !dirty) return;
    setSaving(true);
    setError(null);
    try {
      // 입력칸에 남은 미확정 값도 포함한다(칩 추가를 잊고 저장하는 경우 — ProjectCreateModal 관례)
      const pending = input.split(',').map((x) => x.trim()).filter((x) => x && x.length <= 100);
      const next = [...words];
      pending.forEach((w) => { if (!next.includes(w)) next.push(w); });

      const d = await patchProject(projectId, { keywords: next }, getAccessToken);
      if (d === null) return; // 401 → /signin

      setWords(next);
      setBaseline(next); // 여기서만 앞으로 민다 — 다시 고치면 dirty 가 서고 [저장]이 열린다
      setInput('');
      setSaved(true);
      if (onKeywordsSaved) onKeywordsSaved(next); // 패널 상세만 갱신(이 창은 ref 로 무시)
      await load(); // needs_repromote 를 다시 읽는다 — 이것이 재등록 대상을 만든다
    } catch (e) {
      setError(e.message || '공통 키워드를 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || promoting;

  const footer = (
    <div className="flex w-full items-center justify-between gap-2">
      <span className="min-w-0 flex-1 text-xs text-amber-700">
        {dirty ? '공통 키워드가 저장되지 않았습니다 — 저장해야 등록에 반영됩니다.' : ''}
      </span>
      <div className="flex shrink-0 items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>취소</Button>
        <Button
          size="sm"
          onClick={() => onPromote(targets.map((p) => p.ocr_prob_id))}
          loading={promoting}
          disabled={busy || targets.length === 0 || dirty}
        >
          {/* 신규와 갱신을 갈라 말한다 — 「21건 등록」만 뜨면 이미 등록된 것이 함께 간다는 사실이 숨는다 */}
          {stage.repromote.length > 0
            ? `신규 ${stage.fresh.length} · 갱신 ${stage.repromote.length}건 등록 실행`
            : `${targets.length}건 등록 실행`}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="등록 전 공통 라벨 확인"
      size="md"
      closeOnEsc={!busy}
      closeOnBackdropClick={false}
      footer={footer}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          검수와 AI 분석이 끝난 문제를 문제은행에 등록합니다. 아래 공통 키워드가 등록될 문제 전부에
          라벨로 함께 실립니다.
        </p>

        {lockWarning && (
          <div className="rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">{lockWarning}</div>
        )}

        {/* 공통 키워드 — 이제 여기서 고친다 */}
        <section className="rounded-xl border border-gray-200 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-gray-500">프로젝트 공통 키워드</span>
            <Button size="sm" variant="secondary" onClick={saveKeywords} loading={saving} disabled={!dirty || saving}>
              {saved && !dirty ? '저장됨' : '저장'}
            </Button>
          </div>

          {words.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1">
              {words.map((w) => (
                <span key={w} className={`${CHIP} bg-primary/10 text-primary`}>
                  {w}
                  <button
                    type="button"
                    title={`「${w}」를 뺍니다`}
                    onClick={() => { setWords((prev) => prev.filter((x) => x !== w)); setSaved(false); }}
                    disabled={busy}
                    className="ml-0.5 text-primary/60 hover:text-primary disabled:opacity-40"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="키워드를 넣고 Enter (콤마로도 나눕니다)"
            disabled={busy}
            className="w-full rounded-lg border px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
          />

          {/* 저장은 키워드만 고친다. 은행에 반영하는 것은 [등록 실행]이다 —
              그 순서를 화면이 말하지 않으면 「저장했는데 왜 그대로지」가 된다. */}
          <p className="mt-1.5 text-[11px] text-gray-400">
            {promotedCount + stage.repromote.length > 0
              ? '저장한 뒤 아래 [등록 실행]을 누르면 이미 등록된 문제의 라벨에도 반영됩니다 — 라벨만 다시 계산되고, OCR 쪽을 고치지 않았다면 본문·정답·풀이는 그대로입니다.'
              : '바뀐 키워드는 등록할 때 문제에 붙습니다.'}
          </p>

          {stage.repromote.length > 0 && (
            <p className="mt-1 text-[11px] text-primary">
              반영을 기다리는 등록분 {stage.repromote.length}건 — [등록 실행]에 함께 들어갑니다.
            </p>
          )}

        </section>

        {/* 등록 대상 */}
        <div className="rounded-xl bg-gray-50 p-3 text-xs">
          {loading ? (
            <span className="flex items-center gap-2 text-gray-500">
              <Loader2 className="size-3.5 animate-spin" /> 대상을 세는 중…
            </span>
          ) : (
            <>
              <div className="text-gray-800">
                이번에 등록할 문제 <span className="font-bold">{targets.length}</span>건
                <span className="text-gray-500"> — AI 분석이 끝나고 그 결과까지 승인된 것</span>
              </div>
              {/* 0건이 자주 나온다 — 분석이 끝나면 그 문제는 검수대기로 되돌아오기 때문이다.
                  그때 「없습니다」로만 끝내면 사람은 무엇을 해야 할지 알 수 없다. 나머지가 어느 단계에
                  있는지 세어 보이고, 단계마다 할 일을 적는다. */}
              {stage.total > targets.length && (
                <div className="mt-1.5 space-y-0.5">
                  <div className="text-gray-500">나머지 {stage.total - targets.length}건은 이렇습니다</div>
                  {STAGES.filter((k) => stage.counts[k] > 0).map((k) => (
                    <div key={k} className="text-gray-600">
                      · <span className="font-medium">{STAGE_TEXT[k].label} {stage.counts[k]}건</span>
                      {' — '}{STAGE_TEXT[k].how}
                    </div>
                  ))}
                </div>
              )}
              {stage.analysisUnknown && (
                <div className="mt-1 text-danger">
                  문제 목록이 분석 상태를 주지 않아 대상을 가릴 수 없습니다 — 백엔드 확인이 필요합니다.
                </div>
              )}
              {truncated && (
                <div className="mt-1 text-amber-700">문제 목록을 일부만 읽었습니다 — 대상 수가 실제보다 적을 수 있습니다.</div>
              )}
            </>
          )}
        </div>

        {error && <p className="text-xs text-danger">{error}</p>}

        <p className="text-[11px] text-gray-400">
          인덱스(원본 등장 순서)는 라벨로 붙지 않습니다 — 지면 번호와 섞이지 않게 끕니다.
          등록은 위 <span className="font-medium">{targets.length}건</span>에 대해서만 실행됩니다.
        </p>
      </div>
    </Modal>
  );
}

export default PromoteLabelModal;
